import { supabase } from './client';
import { getDbTransportType } from '@/lib/constants/transportTypes';
import { getProductById } from './products';

// NEW: Get all open carts for a user (one per store)
export const getOpenCartsForUser = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('carts')
        .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at,stores!fk_carts_store_id(id,name,slug),cart_items(id,product_id,quantity,unit_price,details,created_at,updated_at,products(id,title,description,main_image_url,cover_url,image_urls,store_id,fixed_sale_price,stock,is_digital,product_type,details,is_rental,is_store_rental))')
      .eq('user_id', userId)
      .eq('status', 'open')
      .not('store_id', 'is', null)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('❌ getOpenCartsForUser query error:', error);
      throw error;
    }
    if (import.meta.env.DEV) {
    }
    return data || [];
  } catch (e) {
    console.error('❌ getOpenCartsForUser error', e);
    // Fallback: try without the new fields
    try {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('carts')
        .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at,stores!fk_carts_store_id(id,name,slug),cart_items(id,product_id,quantity,unit_price,details,created_at,updated_at,products(id,title,description,main_image_url,cover_url,image_urls,store_id,fixed_sale_price,stock,details,is_rental))')
        .eq('user_id', userId)
        .eq('status', 'open')
        .not('store_id', 'is', null)
        .order('created_at', { ascending: false });
      if (fallbackError) throw fallbackError;
      if (import.meta.env.DEV) {
      }
      return fallbackData || [];
    } catch (fallbackErr) {
      console.error('❌ Fallback query also failed:', fallbackErr);
      return [];
    }
  }
};

export const getCartForUser = async (userId: string, storeId?: string) => {
  try {
    let q = supabase
      .from('carts')
      .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at,cart_items(id,product_id,quantity,unit_price,details,created_at,updated_at,products(id,title,description,main_image_url,cover_url,image_urls,store_id,fixed_sale_price,stock,is_digital,product_type,details,is_rental))')
      .eq('user_id', userId)
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(1);
    if (storeId) q = (q as any).eq('store_id', storeId);
    const { data, error } = await q.maybeSingle();
    if (error) {
      console.error('❌ getCartForUser query error:', error);
      throw error;
    }
    if (import.meta.env.DEV) {
    }
    return data;
  } catch (e) {
    console.error('❌ getCartForUser error', e);
    // Fallback: try without the new fields
    try {
      let q = supabase
        .from('carts')
        .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at,cart_items(id,product_id,quantity,unit_price,details,created_at,updated_at,products(id,title,description,main_image_url,cover_url,image_urls,store_id,fixed_sale_price,stock,details,is_rental))')
        .eq('user_id', userId)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1);
      if (storeId) q = (q as any).eq('store_id', storeId);
      const { data: fallbackData, error: fallbackError } = await q.maybeSingle();
      if (fallbackError) throw fallbackError;
      if (import.meta.env.DEV) {
      }
      return fallbackData;
    } catch (fallbackErr) {
      console.error('❌ Fallback query also failed:', fallbackErr);
      return null;
    }
  }
};

const ensureOpenCart = async (userId: string, storeId?: string) => {
  // try get existing
  const existing = await getCartForUser(userId, storeId);
  if (existing) return existing;
  const payload: any = { user_id: userId };
  if (storeId) payload.store_id = storeId;
  const { data, error } = await supabase.from('carts').insert(payload).select().single();
  if (error) throw error;
  return data;
};

// NEW: Export helper to get or create cart for batch operations
export const getOrCreateCartForStore = async (userId: string, storeId: string) => {
  return await ensureOpenCart(userId, storeId);
};

// Force-create a new open cart in the given store, without reusing existing ones.
export const createNewCartForStore = async (userId: string, storeId: string) => {
  if (!userId) throw new Error('userId is required');
  if (!storeId) throw new Error('storeId is required');

  const { data, error } = await supabase
    .from('carts')
    .insert({ user_id: userId, store_id: storeId, status: 'open' })
    .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at')
    .single();

  if (error) throw error;
  return data;
};

export const addCartItem = async (userId: string, productId: string, quantity: number = 1, cartId?: string) => {
  try {
    // fetch product to validate it belongs to a store
    const prod = await getProductById(productId);
    if (!prod) throw new Error('Producto no encontrado');
    if (!prod.store_id) throw new Error('Solo se permiten carritos para productos que pertenecen a una tienda');

    // Use provided cartId only if it belongs to the same store as the product.
    // If there's a mismatch (or no cartId), always use the product's store to find/create the correct cart.
    let cart: { id: string };
    if (cartId) {
      const { data: cartRow } = await supabase
        .from('carts')
        .select('id, store_id')
        .eq('id', cartId)
        .maybeSingle();
      if (cartRow && cartRow.store_id === prod.store_id) {
        cart = { id: cartId };
      } else {
        // Wrong store — find/create the correct cart for this product's store
        cart = await ensureOpenCart(userId, prod.store_id);
      }
    } else {
      cart = await ensureOpenCart(userId, prod.store_id);
    }

    // unit price snapshot
    const unitPrice = (prod.fixed_sale_price ?? (prod as any).buy_now_price ?? 0) as number;

    // check existing item
    const { data: existingItems } = await supabase
      .from('cart_items')
      .select('*')
      .eq('cart_id', cart.id)
      .eq('product_id', productId);

    if (existingItems && existingItems.length > 0) {
      const item = existingItems[0];
      const newQty = item.quantity + quantity;
      
      // Validate stock if product has stock tracking
      if (prod.stock !== null && prod.stock !== undefined) {
        if (newQty > prod.stock) {
          throw new Error(`Stock insuficiente. Solo hay ${prod.stock} unidades disponibles`);
        }
      }
      
      const { data, error } = await supabase
        .from('cart_items')
        .update({ quantity: newQty, unit_price: unitPrice, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    }

    // Validate stock for new item
    if (prod.stock !== null && prod.stock !== undefined) {
      if (quantity > prod.stock) {
        throw new Error(`Stock insuficiente. Solo hay ${prod.stock} unidades disponibles`);
      }
    }

    const { data, error } = await supabase
      .from('cart_items')
      .insert({ cart_id: cart.id, product_id: productId, quantity, unit_price: unitPrice })
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (e) {
    console.error('addCartItem error', e);
    throw e;
  }
};

export const updateCartItem = async (cartItemId: string, quantity: number) => {
  try {
    if (quantity <= 0) {
      const { error } = await supabase.from('cart_items').delete().eq('id', cartItemId);
      if (error) throw error;
      return null;
    }
    const { error } = await supabase
      .from('cart_items')
      .update({ quantity, updated_at: new Date().toISOString() })
      .eq('id', cartItemId);
    if (error) throw error;
    return { id: cartItemId, quantity };
  } catch (e) {
    console.error('updateCartItem error', e);
    throw e;
  }
};

export const removeCartItem = async (cartItemId: string) => {
  try {
    const { error } = await supabase.from('cart_items').delete().eq('id', cartItemId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error('removeCartItem error', e);
    throw e;
  }
};

/**
 * Update passenger details for a cart item
 * Details structure:
 * {
 *   passengers: [{ id, name, documentNumber, nationality }],
 *   selectedDate: "YYYY-MM-DD",
 *   endDate?: "YYYY-MM-DD",
 *   pickupLocation?: { lat, lng, address },
 *   rentalDays?: number,
 *   billing_mode?: "per_day" | "per_unit"
 * }
 */
export const updateCartItemDetails = async (cartItemId: string, details: Record<string, any>) => {
  try {
    const { error } = await supabase
      .from('cart_items')
      .update({ details, updated_at: new Date().toISOString() })
      .eq('id', cartItemId);
    if (error) throw error;
    return { id: cartItemId, details };
  } catch (e) {
    console.error('updateCartItemDetails error', e);
    throw e;
  }
};

/**
 * Get passenger details for a cart item
 */
export const getCartItemDetails = async (cartItemId: string) => {
  try {
    const { data, error } = await supabase
      .from('cart_items')
      .select('details')
      .eq('id', cartItemId)
      .single();
    if (error) throw error;
    return data?.details || null;
  } catch (e) {
    console.error('getCartItemDetails error', e);
    return null;
  }
};

/**
 * Update passenger details for ALL items in a cart
 * Useful for tours/rentals where passengers and dates apply to all items
 */
export const updateCartItemsDetailsForStore = async (
  userId: string,
  storeId: string,
  details: Record<string, any>
) => {
  try {
    console.log('💾 [carts.ts] updateCartItemsDetailsForStore called with:', {
      userId,
      storeId,
      detailsKeys: Object.keys(details),
      passengerCount: details.passengers?.length,
      tourDaysCount: details.tourDays?.length,
    });

    // Get the cart for this user and store
    const cart = await getCartForUser(userId, storeId);
    if (!cart || !cart.cart_items) {
      console.error('❌ [carts.ts] No cart found for user and store');
      return { success: false, updated: 0 };
    }

    console.log('🔍 [carts.ts] Found cart with items:', {
      cartId: cart.id,
      itemCount: cart.cart_items.length,
    });

    // Update all items in this cart
    const updatePromises = cart.cart_items.map((item: any) => {
      console.log('📝 [carts.ts] Updating cart item:', {
        itemId: item.id,
        productId: item.product_id,
      });
      return supabase
        .from('cart_items')
        .update({ details, updated_at: new Date().toISOString() })
        .eq('id', item.id);
    });

    const results = await Promise.all(updatePromises);
    
    console.log('📊 [carts.ts] Update results:', {
      totalItems: results.length,
      successCount: results.filter(r => !r.error).length,
      errors: results.filter(r => r.error).map(r => r.error),
    });

    // Check for errors
    const hasError = results.some(r => r.error);
    if (hasError) {
      const errors = results.filter(r => r.error).map(r => r.error);
      console.error('❌ [carts.ts] Some items failed to update:', errors);
      throw new Error('Failed to update some items');
    }

    console.log('✅ [carts.ts] All items updated successfully');
    return { success: true, updated: cart.cart_items.length };
  } catch (e) {
    console.error('❌ updateCartItemsDetailsForStore error', e);
    throw e;
  }
};

export const clearCart = async (userId: string, storeId?: string) => {
  try {
    const cart = await getCartForUser(userId, storeId);
    if (!cart) return null;
    // remove items
    const { error } = await supabase.from('cart_items').delete().eq('cart_id', cart.id);
    if (error) throw error;
    // optionally mark cart as abandoned/checked_out
    const { data, error: e2 } = await supabase.from('carts').update({ status: 'checked_out', updated_at: new Date().toISOString() }).eq('id', cart.id).select().single();
    if (e2) throw e2;
    return data;
  } catch (e) {
    console.error('clearCart error', e);
    throw e;
  }
};

export interface CheckoutBankData {
  buyerCbu?: string;
  buyerBankName?: string;
  buyerAccountHolder?: string;
  paymentMethod?: string;
  notes?: string | null;
  payment_proof_url?: string | null;
  delivery_fee?: number | null;
  delivery_distance?: number | null;
  delivery_pickup_address?: string | null;
  delivery_address?: string | null;
  delivery_pickup_latitude?: number | null;
  delivery_pickup_longitude?: number | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
}

export interface CheckoutTargetData {
  cartId?: string;
  sharedToken?: string | null;
}

export const checkoutCart = async (
  userId: string,
  storeId?: string,
  bankData?: CheckoutBankData,
  targetData?: CheckoutTargetData
) => {
  try {
    let cartToCheckout: any = null;
    const requestedCartId = targetData?.cartId;
    const isVirtualSharedCartId = !!requestedCartId && String(requestedCartId).startsWith('shared-cart-');

    // Collaborative cart checkout is no longer supported with token-based system
    if (isVirtualSharedCartId) {
      throw new Error('Los carritos colaborativos ahora se manejan a través del acceso directo, no de tokens.');
    }

    if (requestedCartId && !isVirtualSharedCartId) {
      const { data: cartById, error: cartByIdError } = await supabase
        .from('carts')
        .select('id,user_id,store_id,status,total_amount,notes,created_at,updated_at')
        .eq('id', requestedCartId)
        .eq('user_id', userId)
        .eq('status', 'open')
        .maybeSingle();

      if (cartByIdError) throw cartByIdError;
      cartToCheckout = cartById;
    }

    // Default checkout path (owner cart by store)
    if (!cartToCheckout) {
      cartToCheckout = await getCartForUser(userId, storeId);
    }

    if (!cartToCheckout) throw new Error('No open cart');
    
    // Si no se pasan notas explícitamente, usar las notas guardadas en el carrito
    const notesToUse = bankData?.notes !== undefined ? bankData.notes : (cartToCheckout.notes || null);
    const { data, error } = await supabase.rpc('checkout_cart', {
      p_cart_id: cartToCheckout.id,
      p_buyer_cbu: bankData?.buyerCbu || null,
      p_buyer_bank_name: bankData?.buyerBankName || null,
      p_buyer_account_holder: bankData?.buyerAccountHolder || null,
      p_payment_method: bankData?.paymentMethod || 'bank_transfer',
      p_notes: notesToUse,
      p_delivery_fee: bankData?.delivery_fee ?? null,
      p_delivery_distance: bankData?.delivery_distance ?? null,
      p_delivery_pickup_address: bankData?.delivery_pickup_address ?? null,
      p_delivery_address: bankData?.delivery_address ?? null,
      // NUEVO: coordenadas
      p_delivery_pickup_latitude: bankData?.delivery_pickup_latitude ?? null,
      p_delivery_pickup_longitude: bankData?.delivery_pickup_longitude ?? null,
      p_delivery_latitude: bankData?.delivery_latitude ?? null,
      p_delivery_longitude: bankData?.delivery_longitude ?? null,
      // NUEVO: transport type para matching de couriers (normalizado a valores BD)
      p_delivery_transport_type: getDbTransportType(bankData?.delivery_transport_type ?? null),
    });
    if (process.env.NODE_ENV === 'development') {
    }
    if (error) {
      // If RPC missing or fails, surface error
      throw error;
    }
    return data;
  } catch (e) {
    console.error('checkoutCart error', e);
    // Fallback: rethrow to be handled by caller
    throw e;
  }
};

// Update cart notes
export const updateCartNotes = async (userId: string, storeId: string, notes: string) => {
  try {
    const cart = await getCartForUser(userId, storeId);
    if (!cart) throw new Error('Carrito no encontrado');
    
    const { error } = await supabase
      .from('carts')
      .update({ notes, updated_at: new Date().toISOString() })
      .eq('id', cart.id);
    
    if (error) throw error;
    return { success: true };
  } catch (e) {
    console.error('updateCartNotes error', e);
    throw e;
  }
};

// NEW: Clear cart items for a specific store
export const clearCartItemsForStore = async (userId: string, storeId: string) => {
  try {
    const cart = await getCartForUser(userId, storeId);
    if (!cart) return;

    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('cart_id', cart.id);

    if (error) throw error;
    return { success: true };
  } catch (e) {
    console.error('clearCartItemsForStore error', e);
    throw e;
  }
};

export type InProgressStoreCart = {
  id: string;
  user_id: string;
  store_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  total_amount?: number | null;
  cart_items: Array<{
    id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
  }>;
};

export const getInProgressCartsByStoreId = async (storeId: string): Promise<InProgressStoreCart[]> => {
  try {
    const { data, error } = await supabase
      .from('carts')
      .select('id,user_id,store_id,status,total_amount,created_at,updated_at,cart_items(id,product_id,quantity,unit_price,details)')
      .eq('store_id', storeId)
      .eq('status', 'open')
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return (data || []) as InProgressStoreCart[];
  } catch (e) {
    console.error('getInProgressCartsByStoreId error', e);
    return [];
  }
};

/**
 * Migra items de carrito temporal al carrito real de la BD
 */
export const migrateTempCart = async (userId: string, tempItems: any[], storeId: string) => {
  try {
    if (!tempItems || tempItems.length === 0) {
      return { success: true, migratedCount: 0 };
    }

    
    // FIRST: Clear all existing cart items for this store to do a complete replacement
    // This ensures we don't mix old and new cart items
    try {
      const existingCart = await getCartForUser(userId, storeId);
      if (existingCart && existingCart.cart_items) {
        for (const item of existingCart.cart_items) {
          await supabase.from('cart_items').delete().eq('id', item.id);
        }
      }
    } catch (clearErr) {
      console.warn('[migrateTempCart] Warning clearing existing cart:', clearErr);
      // Continue anyway - might be no existing cart
    }

    // SECOND: Migrate all temp cart items
    let migratedCount = 0;
    for (const item of tempItems) {
      try {
        // Use upsert-style behavior: insert fresh without checking existing
        const prod = await getProductById(item.product_id);
        if (!prod) {
          console.warn('[migrateTempCart] Product not found:', item.product_id);
          continue;
        }
        
        const cart = await ensureOpenCart(userId, storeId);
        const unitPrice = (prod.fixed_sale_price ?? (prod as any).buy_now_price ?? 0) as number;
        
        // Insert fresh item (we already cleared old ones)
        const { error } = await supabase
          .from('cart_items')
          .insert({
            cart_id: cart.id,
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: unitPrice
          });
        
        if (error) throw error;
        migratedCount++;
      } catch (e) {
        console.warn('[migrateTempCart] Failed to migrate item:', item.product_id, e);
        // Continue with next items
      }
    }

    return { success: true, migratedCount };
  } catch (e) {
    console.error('[migrateTempCart] Error during migration:', e);
    throw e;
  }
};

export default {
  getCartForUser,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  clearCartItemsForStore,
  getInProgressCartsByStoreId,
  checkoutCart,
  updateCartNotes,
};
